import { inject, Injectable } from '@angular/core'
import { ApplicationType, ENV, HttpError, HttpService, LoggerService, Service, ServiceType, WebServiceError} from '@danangelus/ngx-app-core'
import { Application } from '../../models/application.model'
import { isServerError, withRetry } from '../utils/retry.utils'
import { GatewayError } from '../../models/gateway.model'
import { Page } from '../../models/page.model'

const log: LoggerService = LoggerService.get('AppManagerService')

/**
 * Service to communicate with the gateway-app-manager on the back-end
 *
 * @author Dan Bennett
 */
@Injectable({
    providedIn: 'root'
})
export class AppManagerService {

    private loginMessage: string | null = null

    private http: HttpService = inject(HttpService)

    async loadEnvironment() {
        try {
            if (!ENV.services?.size || !ENV.services.get(ServiceType.APP_MANAGER) || !ENV.services.get(ServiceType.APP_MANAGER)?.api?.get('load-environment')) {
                // If it's not loaded properly, refresh it based on the current host
                const host = window.location.host
                const baseUrl: string = ENV.configServiceHostMap[host]
                const api: Map<string, string> = new Map<string, string>()
                api.set('load-environment', '/public/application/environment')

                ENV.services.set(ServiceType.APP_MANAGER, { baseUrl: baseUrl, tokenHeader: '', api: api } as Service)
            }

            const response: any = await withRetry(
                () => this.http.get(
                    ServiceType.APP_MANAGER,
                    'load-environment',
                    null,
                    {
                        s: Object.values(ServiceType),
                        a: Object.values(ApplicationType)
                    },
                    false
                ),
                { maxRetries: 3, shouldRetry: isServerError, label: 'loadEnvironment' }
            )
            if (!(response instanceof WebServiceError)) {
                return response
            }
        } catch (err) {
            log.error(`Error loading environment config! ${err}`)
        }
        return ''
    }

    async loadLoginMessage() {
        log.info('loadLoginMessage','Loading login message...')
        try {
            const response: any = await this.http.get(
                ServiceType.APP_MANAGER,
                'load-login-message',
                null,
                null,
                false
            )
            this.setLoginMessage(response.loginMessage)
        } catch (err) {
            log.error(`Error loading login message! ${err}`)
        }
    }

    async storeLoginMessage() {
        log.info('storeLoginMessage',`Updating login message: ${this.getLoginMessage()}`)
        try {
            return this.http.post(
                ServiceType.APP_MANAGER,
                'update-login-message',
                this.getLoginMessage(),
                null,
                null,
                false,
                true
            )
        } catch (err) {
            log.error(`Error loading login message! ${err}`)
            return ""
        }
    }

    setLoginMessage(loginMessage: string) {
        this.loginMessage = loginMessage
    }

    getLoginMessage() {
        return this.loginMessage
    }

    /**
     * Retrieve a collection of Applications with optional querying and sorting
     * to search against:
     * <ul>
     *     <li>Name</li>
     *     <li>Description</li>
     *     <li>Expert Code</li>
     * </ul>
     *
     * If no query is provided, all applications are extracted
     *
     * @param query Optional string value for a query
     * @param pageSize Max results size
     * @param pageNumber Page to retrieve
     * @param sort Column to sort by (inc direction)
     */
    async findApplications(
        query?: string | undefined,
        pageSize?: number | undefined,
        pageNumber?: number | undefined,
        sort?: string | undefined,
    ): Promise<any> {
        try {
            // Hard coding the endpoint for now as there is a weird issue where the api url is not being found and set as undefined
            // const url = ENV.services.get(ServiceType.APP_MANAGER)?.api?.get('get-applications')
            // log.info("findApplications", 'The API URL: ${url}`)
            return this.http.get(
                ServiceType.APP_MANAGER,
                '/app/applications',
                null,{
                    query: query || null,
                    pageSize: pageSize || null,
                    pageNumber: pageNumber || null ,
                    sort: sort || null
                },
                false,
                true
            )
        } catch (err: any) {
            log.error(`There was an error attempting to retrieve application data! ${err}`)
        }
        return []
    }

    /**
     * Retrieve Applications accessible to a User via their groups, including the groups that each Application requires
     */
    async getApplicationsForGroups(): Promise<Map<string, any>[]> {
        let result = []
        try {
            log.info('getApplicationsForGroups', 'Getting Applications available with this User\'s groups')
            result = await this.http.get(
                ServiceType.APP_MANAGER,
                'get-applications-groups',
                null,
                null,
                false,
                true
            )
        } catch (err: any) {
            log.error(`There was an error attempting to retrieve application data with the user's groups! ${err}`)
        }
        return result
    }

    async getAvailableApplicationsForSubUser(): Promise<Map<string, any>[]> {
        let result = []
        try {
            log.info('getApplicationsForGroups', 'Getting Applications available with this User\'s groups')
            result = await this.http.get(
                ServiceType.APP_MANAGER,
                'get-applications-groups-subuser',
                null,
                null,
                false,
                true
            )
        } catch (err: any) {
            log.error(`There was an error attempting to retrieve application data with the user's groups! ${err}`)
        }
        return result
    }

    /**
     * Retrieve the Applications that the logged-in User has access to from the database.
     *
     * The backend will determine which Applications are permitted from the User's registered groups.
     */
    async getDashboard(): Promise<Application[]> {
        const before = Date.now()
        let result = []
        try {
            log.info('getDashboard', 'Getting the permitted Applications for the User')
            result = await this.http.get(
                ServiceType.APP_MANAGER,
                '/app/dashboard/applications',
                null,
                null,
                false,
                true
            )
        } catch (err: any) {
            log.error(`There was an error attempting to retrieve the users dashboard! ${err}`)
        }
        const after = Date.now()
        log.info("getDashboard", `Retrieved ${result.length} items in ${after.valueOf() - before.valueOf()} milliseconds`)
        return result
    }

    /**
     * Call on the App Manager to get an Application that matches the provided {@code expertCode}
     *
     * @param expertCode Expert Code to query for
     */
    async getApplicationByExpertCode(expertCode: string): Promise<Application | null> {
        try {
            log.info('getApplicationByExpertCode', `Finding an Application with expertCode [${expertCode}]`)
            return await this.http.get(ServiceType.APP_MANAGER, 'get-application-expert-code', { expertCode: expertCode }, null, false, true)
        } catch (err: any) {
            log.error(`There was an error attempting to retrieve an Application with expert code [${expertCode}]: ${err}`)
        }
        return null
    }

    /**
     * Retrieve a specific application by its ID in the database
     *
     * @param id The applications ID in the database
     */
    async loadApplication(id: string): Promise<Application | undefined> {
        try {
            log.info('loadApplication', `Loading application with ID: [${id}]`)
            const response: any = await this.http.get(ServiceType.APP_MANAGER, 'load-application', { id }, null, false, true)
            return new Application(response)
        } catch (err: any) {
            log.error(`There was an error attempting to retrieve data for this application! ${err}`)
        }
        return
    }

    /**
     * Create a new application in the database
     *
     * @param application The application to create
     */
    async createApplication(application: Application): Promise<GatewayError | boolean> {
        try {
            log.info('createApplication', 'Creating a new application.')
            const response = await this.http.post(ServiceType.APP_MANAGER, 'create-application', application, null, null, false, true)
            if (response instanceof HttpError) {
                return new GatewayError({
                    error: response.body!.error,
                    message: response.body!.message
                })
            }
            return true
        } catch (err: any) {
            log.error(`There was an error attempting to create a new application! ${err}`)
        }
        return false
    }

    /**
     * Update an existing application in the database
     *
     * @param application The application to update
     */
    async saveApplication(application: Application): Promise<GatewayError | boolean> {
        try {
            log.info('saveApplication', `Saving application with ID: [${application.id}]`)
            const response = await this.http.put(ServiceType.APP_MANAGER, 'save-application', application, { id: application.id }, null, false, true)
            if (response instanceof HttpError) {
                return new GatewayError({
                    error: response.body!.error,
                    message: response.body!.message
                })
            }
            return true
        } catch (err: any) {
            log.error(`There was an error attempting to edit the application! ${err}`)
        }
        return false
    }

    /**
     * Delete an existing application from the database using its ID
     *
     * @param id The applications ID in the database
     */
    async deleteApplication(id: string): Promise<GatewayError | boolean> {
        try {
            log.info('deleteApplication', `Deleting application with ID: [${id}]`)
            const response = await this.http.delete(ServiceType.APP_MANAGER, 'delete-application', { id }, null, false, true)
            if (response instanceof HttpError) {
                return new GatewayError({
                    error: response.body!.error,
                    message: response.body!.message
                })
            }
            return true
        } catch (err: any) {
            log.error(`There was an error attempting to delete the application! ${err}`)
        }
        return false
    }

    async loadPage(id: string): Promise<Page | undefined> {
        try {
            log.info('loadApplication', `Loading application with ID: [${id}]`)
            const response: any = await this.http.get(ServiceType.APP_MANAGER, 'load-page', { id }, null, false, true)
            if (response && !response.error) {
                return new Page(response)
            }
            return undefined
        } catch (err: any) {
            log.error(`There was an error attempting to retrieve data for this application! ${err}`)
        }
        return
    }

    async createPage(page: Page, applicationId: string): Promise<GatewayError | boolean> {
        try {
            log.info('createPage', 'Creating a new page.')
            const response = await this.http.post(ServiceType.APP_MANAGER, 'create-page', page, { applicationId: applicationId }, null, false, true)
            if (response instanceof HttpError) {
                return new GatewayError({
                    error: response.body!.error,
                    message: response.body!.message
                })
            }
            return true
        } catch (err: any) {
            log.error(`There was an error attempting to create a new page! ${err}`)
        }
        return false
    }

    async savePage(page: Page, applicationId: string) {
        try {
            log.info('savePage', `Saving page with ID: [${page.id}]`)
            const response = await this.http.put(ServiceType.APP_MANAGER, 'save-page', page, { id: page.id, applicationId: applicationId }, null, false, true)
            if (response instanceof HttpError) {
                return new GatewayError({
                    error: response.body!.error,
                    message: response.body!.message
                })
            }
            return true
        } catch (err: any) {
            log.error(`There was an error attempting to edit the page! ${err}`)
        }
        return false
    }

    async deletePage(id: string): Promise<GatewayError | boolean> {
        try {
            log.info('deletePage', `Deleting page with ID: [${id}]`)
            const response = await this.http.delete(ServiceType.APP_MANAGER, 'delete-page', { id }, null, false, true)
            if (response instanceof HttpError) {
                return new GatewayError({
                    error: response.body!.error,
                    message: response.body!.message
                })
            }
            return true
        } catch (err: any) {
            log.error(`There was an error attempting to delete the page! ${err}`)
        }
        return false
    }

    async getSurveyMetrics(): Promise<GatewayError | any[]> {
        try {
            log.info('getSurveyMetrics', 'Getting Dealer Survey Metrics')
            const response = await this.http.get(ServiceType.APP_MANAGER, 'get-survey-metrics', null, null, false, true)
            if (response instanceof HttpError) {
                log.error(`Error received from App Manager with Error: ${response.body.error} and Message: ${response.body.message}`)
                return new GatewayError({
                    error: response.body.error,
                    message: response.body.message
                })
            }
            return response
        } catch (err: any) {
            log.error(`Unexpected error occurred when getting survey data: ${err}`)
        }
        return []
    }

    async getPartAccMetrics(): Promise<GatewayError | any[]> {
        try {
            log.info('getPartAccMetrics', 'Getting PartAcc Metrics')
            const response = await this.http.get(ServiceType.APP_MANAGER, 'get-parts-acc-metrics', null, null, false, true)
            if (response instanceof HttpError) {
                return new GatewayError({
                    error: response.body!.error,
                    message: response.body.message
                })
            }
            return response
        } catch (err: any) {
            log.error(`Unexpected error occurred when getting Part Acc data: ${err}`)
        }
        return []
    }

    async getVehicleSalesMetrics(): Promise<GatewayError | any[]> {
        try {
            log.info('getVehicleSalesMetrics', 'Getting VehicleSale Metrics')
            const response = await this.http.get(ServiceType.APP_MANAGER, 'get-vehicle-sales-metrics', null, null, false, true)
            if (response instanceof HttpError) {
                return new GatewayError({
                    error: response.body!.error,
                    message: response.body.message
                })
            }
            return response
        } catch (err: any) {
            log.error(`Unexpected error occurred when getting VehicleSale data: ${err}`)
        }
        return []
    }
}
